import type { WazuhAgentConnection, WazuhAgentHealth } from '@bartizo/shared';
import type { ReactNode } from 'react';
import { formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';
import { Facts } from '../ui/DataTable.js';

/**
 * A server's Wazuh agent as the central manager sees it (P11.8.5).
 *
 * Read by the API from the manager every minute, not reported by the server: a
 * Wazuh agent somebody stopped shows here as disconnected whatever the server
 * says. A state is a problem once it has lasted two minutes, since the agent
 * restarts by itself for each new set of watched paths.
 */

const BADGE: Readonly<Record<WazuhAgentConnection, { tone: string; text: string }>> = {
  active: { tone: 'badge-success', text: 'Active' },
  pending: { tone: 'badge-muted', text: 'Connecting' },
  disconnected: { tone: 'badge-danger', text: 'Disconnected' },
  never_connected: { tone: 'badge-warning', text: 'Never connected' },
  not_enrolled: { tone: 'badge-warning', text: 'Not enrolled' },
};

const PROBLEM: Readonly<Record<NonNullable<WazuhAgentHealth['problem']>, string>> = {
  disconnected:
    'The central server has not heard from this server’s Wazuh agent, so changes to watched files here are not reported. Check that wazuh-agent is running on the server (systemctl status wazuh-agent) and can reach the central server on 1514/tcp.',
  never_connected:
    'The agent enrolled but has never reported, so nothing here is watched for file changes. Check that the server can reach the central server on 1514/tcp.',
  not_enrolled:
    'This server asked to enrol its Wazuh agent, and the manager has no agent of its name. Running the agent installer again on the server enrols it.',
};

const when = (at: string | null, timezone: string): ReactNode =>
  at === null ? (
    <span className="muted">never</span>
  ) : (
    <span title={formatInstant(at, { timezone })}>{formatRelative(at)}</span>
  );

export function WazuhAgentCard({
  agent,
  timezone,
}: {
  agent: WazuhAgentHealth;
  timezone: string;
}): ReactNode {
  const badge = BADGE[agent.connection];
  return (
    <Card
      title="Wazuh agent"
      description="File integrity monitoring on this server, as the central server's Wazuh manager sees it."
      aside={<span className={`badge ${badge.tone}`}>{badge.text}</span>}
    >
      {agent.problem !== null && (
        <Alert tone={agent.problem === 'disconnected' ? 'danger' : 'warning'}>
          {PROBLEM[agent.problem]}
        </Alert>
      )}
      {agent.stale && (
        <Alert tone="warning">
          The central server could not ask its Wazuh manager since {formatRelative(agent.checkedAt)}
          , so this may no longer be so. System health on the overview says why.
        </Alert>
      )}
      <Facts
        items={[
          [
            'State',
            <>
              {badge.text.toLowerCase()} since {when(agent.since, timezone)}
            </>,
          ],
          ['Last keep-alive', when(agent.lastKeepAlive, timezone)],
          ['Version', agent.version ?? <span className="muted">unknown</span>],
          ['Agent id', agent.agentId ?? <span className="muted">none</span>],
          ['Enrolled', when(agent.registeredAt, timezone)],
          ['Read', when(agent.checkedAt, timezone)],
        ]}
      />
    </Card>
  );
}
